import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import { AgGridReact } from '@ag-grid-community/react';
import { Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/styles';
import { useAgGrid } from '@tassat/ag-grid';
import {
    ActionTypes,
    AgGridColumnDef,
    ClientMessagesStatusEnum,
    ClientMessagesStatusMap,
    ClientMessagesTypeEnum,
    ClientMessagesTypeMap,
    doesWorkflowRejectCodeExist
} from '@tassat/common-definitions';
import { ClientMessageRecord, ClientMessageReq } from '@tassat/fred-definitions';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import { noop } from 'lodash';
import React from 'react';

import { AG_GRID_SMALL_HEADER_HEIGHT } from 'Lib/constant';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import Worker from 'Lib/worker/PrimusWorker';
import { useAppSelector } from 'Reducers/store/hooks';
import { getClientMessageHistoryArray } from 'Selectors/clientMessages';
import ExternalFilter from 'SharedComponents/agGrid/filters/externalFilter';
import ServerSort from 'SharedComponents/agGrid/serverSort';
import ClientMessageContextMenuRenderer from 'SharedComponents/cellRenderComponent/clientMessageContextMenuRenderer';
import DateCellRender from 'SharedComponents/cellRenderComponent/datecellrender';
import ExternalFilterHeaderComponent from 'SharedComponents/cellRenderComponent/externalFilterHeaderComponent';
import { MarkdownConversionCellRenderer } from 'SharedComponents/cellRenderComponent/MarkdownCoversionCellrender';
import ClientMessageContentDetails from 'SharedComponents/clientMessages/clientMessageContentDetails';
import ClientMessageDetailsModal from 'SharedComponents/modals/clientMessageDetailsModal';

import { clientMessageStatusOptions, clientMessageTypeOptions, getMappedColumn } from './gridOptions';

import type { ClientMessagesAgGridProps } from '.';

export function ClientMessagesAgGrid(props: ClientMessagesAgGridProps) {
    const {
        editClientMessage,
        tableOptions
    } = props;

    const clientMessages = useAppSelector((state) => getClientMessageHistoryArray(state));

    const theme = useTheme<ApplicationMuiTheme>();
    const [ gridApi, onGridReady ] = useAgGrid();
    const [ selectedClientMessageId, setSelectedClientMessageId ] = React.useState<string>(null);
    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);
    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    React.useEffect(() => {
        gridApi?.api.onFilterChanged();
    }, [ tableOptions.TableFilters.length, tableOptions.Sort, tableOptions.DefaultState ]);

    const openEditForm = (message: ClientMessageRecord) => {
        editClientMessage(message);

        return true;
    };

    const validateCancelMessage = (message: ClientMessageRecord) => {
        const bodyElement = (
            <Grid
                container
                direction='column'
            >
                <Typography
                    style={{
                        color: theme?.app?.applicationColors.enumColors.greyLight ||
                            '#a7a7a7',
                        fontFamily: 'corporate-s,sans-serif',
                        paddingBottom: '.5rem'
                    }}
                    variant='body1'
                >
                    Please confirm canceling the below {message.ClientMsgType === ClientMessagesTypeEnum.ADVISORY ?
                        'advisory'
                        : message.ClientMsgType === ClientMessagesTypeEnum.BANNER ?
                            'banner'
                            : ''} message.
                </Typography>
                <ClientMessageContentDetails selectedMessage={message} />
            </Grid>
        );

        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: message,
                actionType: ActionTypes.cancel,
                confirmTitle: 'Confirm Canceling Message',
                confirmBody: bodyElement
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const cancelMessage = async (message: ClientMessageRecord) => {
        try {
            const sendValues: Partial<ClientMessageReq> = {
                ClientMsgType: message.ClientMsgType,
                Message: message.Message,
                StartAt: message.StartAt,
                EndAt: message.EndAt,
                Status: ClientMessagesStatusEnum.CANCELLED
            };

            const response = await Worker.getInstance()
                .updateClientMessage(message.MessageId, { ...sendValues });

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: 'Successfully Canceled Message',
                        successBody: 'Successfully canceled the selected message.'
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: 'Failed to Cancel Message',
                        failureBody: 'Could not cancel the selected message.'
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: 'Failed to Cancel Message',
                    failureBody: 'Something went wrong canceling the selected message.'
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                setSelectedItem: noop,
                preActionValidationAndConfig: {
                    [ActionTypes.edit]: openEditForm,
                    [ActionTypes.cancel]: validateCancelMessage
                },
                onConfirm: {
                    [ActionTypes.edit]: null,
                    [ActionTypes.cancel]: cancelMessage
                },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    const clientMessagesColumnDefs: AgGridColumnDef[] = React.useMemo(() => {
        return [
            {
                field: 'Status',
                headerName: 'Status',
                valueGetter: (params) => ClientMessagesStatusMap[params.data.Status],
                headerComponent: 'externalFilterHeader',
                headerComponentParams: { getMappedColumn },
                filter: 'externalFilter',
                filterParams: {
                    getMappedColumn,
                    inputType: 'select',
                    selectOptions: clientMessageStatusOptions
                },
                menuTabs: ['filterMenuTab'],
                flex: 4
            },
            {
                field: 'ClientMsgType',
                headerName: 'Type',
                valueGetter: (params) => ClientMessagesTypeMap[params.data.ClientMsgType],
                headerComponent: 'externalFilterHeader',
                headerComponentParams: { getMappedColumn },
                filter: 'externalFilter',
                filterParams: {
                    getMappedColumn,
                    inputType: 'select',
                    selectOptions: clientMessageTypeOptions
                },
                menuTabs: ['filterMenuTab'],
                flex: 4
            },
            {
                field: 'CreatedAt',
                headerName: 'Created At',
                cellRenderer: 'dateCellRenderer',
                cellRendererParams: { align: 'flex-start' },
                headerComponent: 'externalFilterHeader',
                headerComponentParams: { getMappedColumn },
                filter: 'serverSort',
                filterParams: { getMappedColumn },
                menuTabs: ['filterMenuTab'],
                flex: 5
            },
            {
                field: 'CreatedByUsername',
                headerName: 'Created By',
                headerComponent: 'externalFilterHeader',
                headerComponentParams: { getMappedColumn },
                filter: 'externalFilter',
                filterParams: {
                    getMappedColumn,
                    inputType: 'text'
                },
                menuTabs: ['filterMenuTab'],
                flex: 10
            },
            {
                field: 'UpdatedAt',
                headerName: 'Modified At',
                cellRenderer: 'dateCellRenderer',
                cellRendererParams: { align: 'flex-start' },
                headerComponent: 'externalFilterHeader',
                headerComponentParams: { getMappedColumn },
                filter: 'serverSort',
                filterParams: { getMappedColumn },
                menuTabs: ['filterMenuTab'],
                flex: 5
            },
            {
                field: 'ModifiedByUsername',
                headerName: 'Modified By',
                headerComponent: 'externalFilterHeader',
                headerComponentParams: { getMappedColumn },
                filter: 'externalFilter',
                filterParams: {
                    getMappedColumn,
                    inputType: 'text'
                },
                menuTabs: ['filterMenuTab'],
                flex: 10
            },
            {
                field: 'ApprovedByUsername',
                headerName: 'Approved By',
                headerComponent: 'externalFilterHeader',
                headerComponentParams: { getMappedColumn },
                filter: 'externalFilter',
                filterParams: {
                    getMappedColumn,
                    inputType: 'text'
                },
                menuTabs: ['filterMenuTab'],
                flex: 10
            },
            {
                field: 'Message',
                headerName: 'Message Content',
                cellStyle: { 'white-space': 'normal' },
                cellRenderer: 'markdownConversionCellRenderer',
                menuTabs: [],
                autoHeight: true,
                flex: 12
            },
            {
                field: 'StartAt',
                headerName: 'Start Time',
                cellRenderer: 'dateCellRenderer',
                cellRendererParams: { align: 'flex-start' },
                headerComponent: 'externalFilterHeader',
                headerComponentParams: { getMappedColumn },
                filter: 'serverSort',
                filterParams: {
                    getMappedColumn,
                    defaultSortField: true,
                    defaultSortDirection: 'ASC'
                },
                menuTabs: ['filterMenuTab'],
                flex: 5
            },
            {
                field: 'EndAt',
                headerName: 'End Time',
                cellRenderer: 'dateCellRenderer',
                cellRendererParams: { align: 'flex-start' },
                headerComponent: 'externalFilterHeader',
                headerComponentParams: { getMappedColumn },
                filter: 'serverSort',
                filterParams: { getMappedColumn },
                menuTabs: ['filterMenuTab'],
                flex: 5
            },
            {
                field: 'Status',
                headerName: 'Actions',
                cellRenderer: 'actionButtonsCellRenderer',
                menuTabs: [],
                flex: 4
            }
        ];
    }, []);

    const setRowHeight = (params): number => {
        if (params.data.Message) {
            const rowH = 12 * (Math.floor(params.data.Message.length / 28) + 2);

            return rowH < 70 ? 70 : rowH;
        }
    };

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <div
                className='ag-theme-material'
                id='adminUsersGrid'
                style={{
                    height: '100%',
                    borderTop: '1px solid #BFB9B9'
                }}
            >
                <AgGridReact
                    animateRows={false}
                    columnDefs={clientMessagesColumnDefs}
                    columnMenu='legacy'
                    components={{
                        dateCellRenderer: DateCellRender,
                        actionButtonsCellRenderer: ClientMessageContextMenuRenderer,
                        externalFilterHeader: ExternalFilterHeaderComponent,
                        externalFilter: ExternalFilter,
                        serverSort: ServerSort,
                        markdownConversionCellRenderer: MarkdownConversionCellRenderer
                    }}
                    defaultColDef={{
                        resizable: true,
                        filter: false,
                        sortable: false
                    }}
                    enableCellTextSelection={true}
                    getRowHeight={setRowHeight}
                    getRowId={({ data }) => data.MessageId}
                    headerHeight={AG_GRID_SMALL_HEADER_HEIGHT}
                    onCellClicked={({ data, column }) => {
                        if (column.getColDef().headerName === 'Actions') {
                            return;
                        } else {
                            setSelectedClientMessageId(data.MessageId);
                        }
                    }}
                    onGridReady={onGridReady}
                    rowData={clientMessages}
                    suppressCellFocus={true}
                    suppressContextMenu={true}
                />
            </div>
            <ConfirmationWorkflowConsumer />
            <ClientMessageDetailsModal
                handleClose={() => {
                    setSelectedClientMessageId(null);
                }}
                open={Boolean(selectedClientMessageId)}
                selectedMessageId={selectedClientMessageId}
            />
        </ConfirmationWorkflowContext.Provider>
    );
}